/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { PlayerController } from "../BasePlayer";

type PlayerVolumeContextValue = {
  volume: number;
  isMuted: boolean;
  changeVolume: (value: number, byValue?: number) => void;
  toggleMute: () => void;
};

const PlayerVolumeContext = createContext<PlayerVolumeContextValue>(
  {} as PlayerVolumeContextValue,
);

type PlayerVolumeProviderProps = {
  player: PlayerController;
  children: ReactNode;
};

export const PlayerVolumeProvider = ({
  children,
  player,
}: PlayerVolumeProviderProps) => {
  const [isMuted, setIsMuted] = useState<boolean>(player.muted());
  const [volume, setVolume] = useState<number>(player.volume());

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => player.muted(!prev));

    if (isMuted && player.volume() === 0) {
      player.volume(1);
    }
  }, [isMuted, player]);

  const changeVolume = useCallback(
    (value: number, byValue?: number) => {
      setVolume((prev) => {
        const rawValue = byValue
          ? Math.max(0, Math.min(1, prev + byValue))
          : value;
        const newValue = parseFloat(rawValue.toFixed(2));

        player.volume(newValue);
        setIsMuted(player.muted(newValue === 0));

        return newValue;
      });
    },
    [player],
  );

  const onVolumeChanged = useCallback(() => {
    setIsMuted(player.muted());
    setVolume(player.muted() ? 0 : player.volume());
  }, [player]);

  useEffect(() => {
    const unsubscribe = player.onEvent("volumechange", onVolumeChanged);

    return () => unsubscribe();
  }, [onVolumeChanged, player]);

  return (
    <PlayerVolumeContext.Provider
      value={{ volume, isMuted, changeVolume, toggleMute }}
    >
      {children}
    </PlayerVolumeContext.Provider>
  );
};

export const usePlayerVolume = (): PlayerVolumeContextValue => {
  const context = useContext(PlayerVolumeContext);

  if (!context) {
    throw new Error("Component beyond PlayerTimerContext");
  }

  return context;
};
